import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
// New import for phone input with dial codes and flags
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

/**
 * @constant {string[]} businessCategories
 * @description A comprehensive list of common business categories representing widely recognized industries and sectors.
 * This array can be extended or adjusted to fit specific business domain requirements.
 */
const businessCategories = [
  "Agriculture",
  "Automotive",
  "Beauty & Personal Care",
  "Business Services",
  "Construction",
  "Consulting",
  "Consumer Services",
  "Education",
  "Entertainment",
  "Finance & Insurance",
  "Food & Beverage",
  "Healthcare",
  "Hospitality",
  "Information Technology",
  "Manufacturing",
  "Media & Communications",
  "Nonprofit",
  "Real Estate",
  "Retail",
  "Telecommunications",
  "Transportation & Logistics",
  "Travel & Tourism",
  "Wholesale",
];

// Freeze the array to prevent accidental modifications at runtime.
Object.freeze(businessCategories);

const CreateAccountModal = ({
  show,
  onHide,
  accountFormData,
  setAccountFormData,
  accountFormPage,
  setAccountFormPage,
  handleAccountCreationSubmit,
  defaultEmail // Keep receiving defaultEmail prop
}) => {

  // Use effect to pre-fill email when modal opens for the first time if needed
  useEffect(() => {
    if (show && accountFormPage === 1 && defaultEmail) {
       // Only set if the fields are currently empty or match the initial state
       if (!accountFormData.companyEmail) {
         setAccountFormData(prev => ({ ...prev, companyEmail: defaultEmail }));
       }
       if (accountFormData.useCompanyEmailForLogin && !accountFormData.userEmail) {
         setAccountFormData(prev => ({ ...prev, userEmail: defaultEmail }));
       }
    }
    // Reset page to 1 when modal is hidden
    if (!show) {
      setAccountFormPage(1);
    }
  }, [show, defaultEmail, accountFormPage, setAccountFormData, setAccountFormPage]);


  // Update userEmail when useCompanyEmailForLogin changes
  useEffect(() => {
    if (accountFormData.useCompanyEmailForLogin) {
      setAccountFormData(prev => ({ ...prev, userEmail: prev.companyEmail }));
    }
  }, [accountFormData.useCompanyEmailForLogin, accountFormData.companyEmail, setAccountFormData]);


  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static" // Add this prop
      keyboard={false} // Optionally disable closing with Esc key
      centered
      size="lg" // Example size, adjust if needed
    >
      <Modal.Header closeButton>
        <Modal.Title>Create Your Marmasco Account</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4"> {/* Added padding */}
        {/* Progress Indicator (Optional) */}
        <div className="text-center text-muted mb-4">Step {accountFormPage} of 2</div>

        {accountFormPage === 1 && (
          <Form>
            <Form.Group className="mb-3"> {/* Reduced margin */}
              <Form.Label className="fw-bold">Company Name</Form.Label> {/* Added fw-bold */}
              <Form.Control
                type="text"
                placeholder="Enter company name"
                value={accountFormData.companyName}
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    companyName: e.target.value,
                  })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Company Services</Form.Label>
              {/* Use Typeahead for multiple services selection */}
              <Typeahead
                id="company-services-typeahead"
                multiple
                allowNew
                newSelectionPrefix="Add: "
                options={businessCategories}
                placeholder="Select or add services offered"
                onChange={(selected) => {
                  const selections = selected.map((item) =>
                    typeof item === "string" ? item : item.label || item
                  );
                  setAccountFormData({
                    ...accountFormData,
                    serviceOffered: selections,
                  });
                }}
                selected={
                  Array.isArray(accountFormData.serviceOffered)
                    ? accountFormData.serviceOffered
                    : []
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Company Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter company email"
                value={accountFormData.companyEmail || ''} // Ensure controlled component
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    companyEmail: e.target.value,
                  })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Use company email for login" // Simplified label
                checked={accountFormData.useCompanyEmailForLogin}
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    useCompanyEmailForLogin: e.target.checked,
                  })
                }
              />
            </Form.Group>
          </Form>
        )}
        {accountFormPage === 2 && (
          <Form>
            <Row> {/* Use Row/Col for better layout */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">First Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    value={accountFormData.firstName || ""}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        firstName: e.target.value,
                      })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    value={accountFormData.lastName || ""}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        lastName: e.target.value,
                      })
                    }
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            {!accountFormData.useCompanyEmailForLogin && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">User Login Email</Form.Label> {/* Clarified label */}
                <Form.Control
                  type="email"
                  placeholder="Enter user login email"
                  value={accountFormData.userEmail || ''} // Ensure controlled component
                  onChange={(e) =>
                    setAccountFormData({
                      ...accountFormData,
                      userEmail: e.target.value,
                    })
                  }
                  required
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">WhatsApp Number</Form.Label>
              <PhoneInput
                country={"zw"} // Default country
                value={accountFormData.phone || ''} // Ensure controlled component
                onChange={(phone) =>
                  setAccountFormData({ ...accountFormData, phone })
                }
                inputStyle={{ width: "100%", height: "calc(1.5em + 0.75rem + 2px)" }} // Match BS input height
                inputClass="form-control" // Use BS class
                // containerClass="mb-3" // Add margin if needed directly
              />
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {accountFormPage === 1 ? (
            <span
              style={{
                cursor: "pointer",
                color: "#007bff",
                textDecoration: "underline",
                alignSelf: "center",
              }}
              onClick={onHide}
            >
              Already Registered?
            </span>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setAccountFormPage(accountFormPage - 1)}
            >
              Back
            </Button>
          )}
        </div>
        <div>
          {accountFormPage < 2 ? (
            <Button
              variant="primary"
              onClick={() => setAccountFormPage(accountFormPage + 1)}
            >
              Continue
            </Button>
          ) : (
            <Button variant="primary" onClick={handleAccountCreationSubmit}>
              Submit
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

CreateAccountModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  accountFormData: PropTypes.object.isRequired,
  setAccountFormData: PropTypes.func.isRequired,
  accountFormPage: PropTypes.number.isRequired,
  setAccountFormPage: PropTypes.func.isRequired,
  handleAccountCreationSubmit: PropTypes.func.isRequired,
  defaultEmail: PropTypes.string, // Add prop type for defaultEmail
};

export default CreateAccountModal;
